import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import BrandLogo from "@/components/BrandLogo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

export default function Login() {
  const { user, login } = useAuth();
  const [email, setEmail] = useState("admin@mrsenterprise.com");
  const [password, setPassword] = useState("admin123");
  const [loading, setLoading] = useState(false);
  const nav = useNavigate();

  if (user) return <Navigate to="/admin" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome back!");
      nav("/admin");
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[#FDFDF9]">
      {/* Left panel: brand */}
      <div className="hidden lg:flex flex-col justify-between p-12 bg-[#111111] text-white relative overflow-hidden">
        <div className="absolute -top-40 -right-40 w-96 h-96 rounded-full bg-[#C59B27]/10 blur-3xl" />
        <div className="absolute -bottom-40 -left-40 w-96 h-96 rounded-full bg-[#C59B27]/5 blur-3xl" />
        <div className="relative z-10">
          <div className="flex items-center gap-3">
            <BrandLogo alt="MRS" className="w-14 h-14 rounded-lg bg-white p-1" />
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-[#C59B27] font-semibold">Enterprise</div>
              <div className="font-serif text-2xl">MRS HRMS</div>
            </div>
          </div>
        </div>
        <div className="relative z-10 space-y-4">
          <h1 className="font-serif text-5xl leading-tight">
            Payroll & <span className="text-[#C59B27]">People</span><br />in one place.
          </h1>
          <p className="text-slate-300 text-base max-w-md">
            Manage attendance, wages, advances and generate professional salary slips for MRS Enterprise workers — with the 24th–23rd payroll cycle built-in.
          </p>
        </div>
        <div className="relative z-10 text-xs text-slate-400">
          &copy; {new Date().getFullYear()} MRS Enterprise. Malda, West Bengal.
        </div>
      </div>

      {/* Right panel: form */}
      <div className="flex items-center justify-center p-6 lg:p-12">
        <div className="w-full max-w-md space-y-8">
          <div className="lg:hidden flex items-center gap-3 mb-8">
            <BrandLogo alt="MRS" className="w-12 h-12" />
            <div className="font-serif text-2xl">MRS Enterprise HRMS</div>
          </div>
          <div>
            <div className="text-xs uppercase tracking-[0.15em] text-[#C59B27] font-bold mb-2">Admin Console</div>
            <h2 className="font-serif text-4xl text-slate-900">Sign in</h2>
            <p className="mt-2 text-slate-600 text-sm">
              Enter your credentials to access the HRMS dashboard.
            </p>
          </div>
          <form onSubmit={submit} className="space-y-5" data-testid="login-form">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-xs uppercase tracking-wider text-slate-500 font-bold">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="login-email-input"
                required
                className="h-11"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="text-xs uppercase tracking-wider text-slate-500 font-bold">Password</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="login-password-input"
                required
                className="h-11"
              />
            </div>
            <Button
              type="submit"
              disabled={loading}
              data-testid="login-submit-button"
              className="w-full h-11 bg-[#111111] hover:bg-[#C59B27] transition-colors duration-200 text-white font-semibold"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Sign in
            </Button>
          </form>
          <div className="text-xs text-slate-400 border-t border-slate-200 pt-4">
            Default: admin@mrsenterprise.com / admin123
          </div>
        </div>
      </div>
    </div>
  );
}
